import { useId, useRef, useState, type KeyboardEvent } from 'react';
import { Tooltip } from '@/components/ui';
import { cn } from '@/components/ui/cn';
import { barAxis } from './chartData';
import { chartPalette, type ChartState } from './chartPalette';
import { ChartLegend, ChartStatus, ChartTable, ChartTooltipContent } from './ChartParts';

export interface BarCategory {
  key: string;
  label: string;
  longLabel?: string;
}
export interface BarSeries {
  id: string;
  label: string;
  values: readonly (number | null)[];
  color?: 1 | 2 | 3 | 4;
}
export interface BarChartProps {
  label: string;
  categories: readonly BarCategory[];
  series: readonly BarSeries[];
  formatValue?: (value: number) => string;
  formatTick?: (value: number) => string;
  axisLabel?: string;
  /** Hide persistent keyboard instructions in compact widgets, retaining focus help. */
  showKeyboardHint?: boolean;
  state?: ChartState;
  onRetry?: () => void;
}
// String сохраняет кратчайшее точное представление числа, включая очень малые дроби.
const exactNumber = (value: number) => String(value).replace('.', ',').replace(/^-/, '−');
const KEYBOARD_HELP =
  '← → — месяц, Home / End — начало и конец. Короткая черта — ноль. Пунктирный квадрат — нет данных.';
export function BarChart({
  label,
  categories,
  series,
  formatValue = exactNumber,
  formatTick = formatValue,
  axisLabel,
  showKeyboardHint = true,
  state = 'ready',
  onRetry,
}: BarChartProps) {
  const id = useId(),
    refs = useRef<(HTMLButtonElement | null)[]>([]);
  const [active, setActive] = useState<string | null>(null),
    [selected, setSelected] = useState<string | null>(null),
    [focused, setFocused] = useState<string | null>(null),
    [tooltipKey, setTooltipKey] = useState<string | null>(null);
  const axis = barAxis(series.flatMap((s) => categories.map((_, i) => s.values[i] ?? null)));
  const invalid =
    axis.error ??
    (series.some((s) => s.values.length !== categories.length)
      ? 'Количество значений каждой серии должно совпадать с количеством категорий.'
      : series.length > 4
        ? 'На графике можно сравнить до четырёх серий.'
        : new Set(categories.map((c) => c.key)).size !== categories.length ||
            new Set(series.map((s) => s.id)).size !== series.length
          ? 'Идентификаторы категорий и серий должны быть уникальны.'
          : null);
  const empty =
    !categories.length ||
    !series.length ||
    !series.some((s) => categories.some((_, i) => s.values[i] != null));
  const mode = state !== 'ready' ? state : invalid ? 'error' : empty ? 'empty' : 'ready';
  if (mode !== 'ready')
    return (
      <figure aria-label={label} data-slot="bar-chart">
        <ChartStatus
          state={mode}
          kind="bar"
          error={invalid}
          onRetry={onRetry}
          columns={categories.length}
          seriesCount={series.length || 1}
        />
      </figure>
    );
  const range = axis.max - axis.min,
    zero = (axis.max / range) * 100;
  const activeIndex = Math.max(
    0,
    categories.findIndex((c) => c.key === active),
  );
  const selectedIndex = categories.findIndex((c) => c.key === selected);
  const text = (v: number | null | undefined) => (v == null ? 'Нет данных' : formatValue(v));
  const rows = (index: number) => series.map((s) => ({ label: s.label, value: text(s.values[index]) }));
  const move = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const next =
      event.key === 'ArrowRight'
        ? Math.min(categories.length - 1, index + 1)
        : event.key === 'ArrowLeft'
          ? Math.max(0, index - 1)
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? categories.length - 1
              : null;
    if (next === null) return;
    event.preventDefault();
    const node = refs.current[next],
      origin = event.currentTarget;
    node?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'instant' });
    // Scroll закрывает Radix Tooltip: открываем его фокусом после размещения ячейки.
    requestAnimationFrame(() => {
      if (document.activeElement === origin) node?.focus({ preventScroll: true });
    });
  };
  return (
    <figure aria-labelledby={id} data-slot="bar-chart" className="min-w-0 space-y-4">
      <figcaption id={id} className="sr-only">
        {label}
      </figcaption>
      <ChartLegend items={series} />
      <div className="flex min-w-0 gap-2 pt-4">
        <div
          aria-hidden
          className="relative h-56 w-14 shrink-0 text-right text-2xs text-fg-subtle tabular-nums"
        >
          {axisLabel && <span className="absolute -top-5 right-0 whitespace-nowrap">{axisLabel}</span>}
          {axis.ticks.map((t) => (
            <span
              key={t}
              className="absolute right-0 -translate-y-1/2"
              style={{ top: ((axis.max - t) / range) * 100 + '%' }}
            >
              {formatTick(t)}
            </span>
          ))}
        </div>
        <div className="min-w-0 flex-1 overflow-x-auto pb-1" data-chart-scroll="">
          <div style={{ minWidth: categories.length * 52 }}>
            <div
              className="relative flex h-56"
              role="group"
              aria-label={label + ' — месяцы'}
              aria-describedby={id + '-help'}
            >
              <div aria-hidden className="pointer-events-none absolute inset-0">
                {axis.ticks.map((t) => (
                  <span
                    key={t}
                    className={cn(
                      'absolute inset-x-0 border-t',
                      t === 0 ? 'border-line-strong' : 'border-line',
                    )}
                    style={{ top: ((axis.max - t) / range) * 100 + '%' }}
                  />
                ))}
              </div>
              {categories.map((category, i) => (
                <Tooltip
                  key={category.key}
                  variant="data"
                  open={showKeyboardHint ? undefined : tooltipKey === category.key}
                  onOpenChange={
                    showKeyboardHint
                      ? undefined
                      : (open) =>
                          setTooltipKey((current) =>
                            open ? category.key : current === category.key ? null : current,
                          )
                  }
                  content={
                    !showKeyboardHint && focused === category.key ? (
                      <div className="space-y-3">
                        <ChartTooltipContent title={category.longLabel ?? category.label} rows={rows(i)} />
                        <p
                          data-chart-keyboard-hint=""
                          className="border-t border-line pt-2 text-2xs text-fg-muted"
                        >
                          {KEYBOARD_HELP}
                        </p>
                      </div>
                    ) : (
                      <ChartTooltipContent title={category.longLabel ?? category.label} rows={rows(i)} />
                    )
                  }
                >
                  <button
                    type="button"
                    ref={(node) => {
                      refs.current[i] = node;
                    }}
                    data-bar-category={category.key}
                    tabIndex={i === activeIndex ? 0 : -1}
                    aria-label={
                      (category.longLabel ?? category.label) +
                      '. ' +
                      rows(i)
                        .map((r) => r.label + ': ' + r.value)
                        .join('; ')
                    }
                    onKeyDown={(e) => move(e, i)}
                    onFocus={() => {
                      setActive(category.key);
                      setSelected(category.key);
                      if (!showKeyboardHint) {
                        setFocused(category.key);
                        requestAnimationFrame(() => {
                          if (refs.current[i] === document.activeElement) setTooltipKey(category.key);
                        });
                      }
                    }}
                    onBlur={() => {
                      if (!showKeyboardHint) {
                        setFocused(null);
                        setTooltipKey((current) => (current === category.key ? null : current));
                      }
                    }}
                    onClick={() => setSelected(category.key)}
                    className="relative h-full min-w-0 flex-1 rounded-sm hover:bg-hover/50 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none focus-visible:ring-inset"
                  >
                    {series.map((s, j) => {
                      const value = s.values[i],
                        left = 12 + (j * 76) / series.length,
                        width = 76 / series.length - 4;
                      return value == null ? (
                        <span
                          key={s.id}
                          aria-hidden
                          data-bar-missing=""
                          className="absolute size-2 -translate-y-1/2 rounded-xs border border-dashed border-fg-subtle bg-card"
                          style={{ left: left + '%', top: zero + '%' }}
                        />
                      ) : (
                        <span
                          key={s.id}
                          aria-hidden
                          data-bar-value={value}
                          className={cn(
                            'pointer-events-none absolute',
                            chartPalette[(s.color ?? j + 1) - 1],
                            value < 0 ? 'rounded-b-xs' : 'rounded-t-xs',
                            value === 0 && 'h-0.5 -translate-y-1/2',
                          )}
                          style={{
                            left: left + '%',
                            width: width + '%',
                            top: (value >= 0 ? ((axis.max - value) / range) * 100 : zero) + '%',
                            ...(value !== 0 ? { height: (Math.abs(value) / range) * 100 + '%' } : {}),
                          }}
                        />
                      );
                    })}
                  </button>
                </Tooltip>
              ))}
            </div>
            <div aria-hidden className="flex h-8 items-center text-center text-2xs text-fg-subtle">
              {categories.map((c) => (
                <span key={c.key} className="min-w-0 flex-1">
                  {c.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
      {(showKeyboardHint || selectedIndex >= 0) && (
        <div className="min-h-16 rounded-md bg-muted px-3 py-2 text-xs text-fg-muted" data-chart-selection="">
          {selectedIndex >= 0 ? (
            <>
              <p className="font-medium text-fg">
                {categories[selectedIndex].longLabel ?? categories[selectedIndex].label}
              </p>
              <p className="mt-1 flex flex-wrap gap-x-5 gap-y-1 tabular-nums">
                {rows(selectedIndex).map((r, index) => (
                  <span key={index}>
                    {r.label}: <span className="font-medium text-fg">{r.value}</span>
                  </span>
                ))}
              </p>
            </>
          ) : (
            <p>Наведи на месяц или выбери его с клавиатуры: ← →, Home, End.</p>
          )}
        </div>
      )}
      <p id={id + '-help'} className={showKeyboardHint ? 'text-2xs text-fg-subtle' : 'sr-only'}>
        {KEYBOARD_HELP}
      </p>
      <ChartTable
        label={label}
        headers={['Месяц', ...series.map((s) => s.label)]}
        rows={categories.map((c, i) => [c.longLabel ?? c.label, ...series.map((s) => text(s.values[i]))])}
      />
    </figure>
  );
}
